import { loginApi } from '@/api'
import { message } from 'ant-design-vue'
import { QRCODE_POLL_INTERVAL_MS, QRCODE_STATUS } from './constants'
import { isExpiredMessage, resolveAuthPayload } from './authResponse'
import { applyQrcodeAuth } from './authSession'

/**
 * 扫码登录状态机：二维码生成、轮询、过期倒计时
 * 与 Vue 组件解耦，由 onStateChange 同步 UI 状态
 */
export class QrcodeLoginController {
  /**
   * @param {{
   *   getRegcode: () => string,
   *   onStateChange: (patch: object) => void,
   *   onLoginSuccess: (res: object, member: object) => void | Promise<void>,
   *   store: object
   * }} options
   */
  constructor ({ getRegcode, onStateChange, onLoginSuccess, store }) {
    this.getRegcode = getRegcode
    this.onStateChange = onStateChange
    this.onLoginSuccess = onLoginSuccess
    this.store = store
    this._queryTokenTimerId = null
    this._expireTimerId = null
    this._state = this._emptyState()
  }

  _emptyState () {
    return {
      qrcodeStatus: QRCODE_STATUS.WAITING,
      img_url: '',
      scene_value: '',
      expire_seconds: 0,
      qrcodeExpireCountdown: 0
    }
  }

  _patch (patch) {
    this._state = { ...this._state, ...patch }
    this.onStateChange(this._state)
  }

  getState () {
    return { ...this._state }
  }

  dispose () {
    this.stopPolling()
    this.stopExpireTimer()
  }

  reset () {
    this.stopPolling()
    this.stopExpireTimer()
    this._patch(this._emptyState())
  }

  stopPolling () {
    if (this._queryTokenTimerId) {
      clearInterval(this._queryTokenTimerId)
      this._queryTokenTimerId = null
    }
  }

  stopExpireTimer () {
    if (this._expireTimerId) {
      clearInterval(this._expireTimerId)
      this._expireTimerId = null
    }
  }

  _markExpired () {
    this._patch({ qrcodeStatus: QRCODE_STATUS.EXPIRED })
    this.stopPolling()
    this.stopExpireTimer()
    message.warning('二维码已过期，请刷新')
  }

  async refresh () {
    this.stopPolling()
    this.stopExpireTimer()
    this._patch({
      ...this._emptyState(),
      qrcodeStatus: QRCODE_STATUS.WAITING
    })

    try {
      const qrcodeParams = {}
      const regcode = this.getRegcode()
      if (regcode) {
        qrcodeParams.regcode = regcode
      }
      const res = await loginApi.generateQrcode(qrcodeParams)

      if (!res || !res.data) {
        message.error('生成二维码失败，请重试')
        this._patch({ qrcodeStatus: QRCODE_STATUS.WAITING })
        return
      }

      const qrcodeData = res.data
      const expire_seconds = qrcodeData.expire_seconds || 0
      const img_url = qrcodeData.img_url || ''
      const scene_value = qrcodeData.scene_value || ''

      if (!img_url || !scene_value) {
        message.error('生成二维码失败，数据不完整')
        this._patch({ qrcodeStatus: QRCODE_STATUS.WAITING })
        return
      }

      this._patch({
        expire_seconds,
        img_url,
        scene_value,
        qrcodeStatus: QRCODE_STATUS.READY,
        qrcodeExpireCountdown: expire_seconds
      })

      this._startExpireTimer()
      this._startPolling()
    } catch (err) {
      console.error('生成二维码失败', err)
      this._patch({ qrcodeStatus: QRCODE_STATUS.WAITING })
      message.error(err?.msg || err?.message || '生成二维码失败，请重试')
    }
  }

  _startPolling () {
    this.stopPolling()
    this._pollOnce()
    this._queryTokenTimerId = setInterval(() => {
      this._pollOnce()
    }, QRCODE_POLL_INTERVAL_MS)
  }

  _startExpireTimer () {
    this.stopExpireTimer()
    this._expireTimerId = setInterval(() => {
      const countdown = this._state.qrcodeExpireCountdown
      if (countdown > 0) {
        this._patch({ qrcodeExpireCountdown: countdown - 1 })
      } else {
        this._markExpired()
      }
    }, 1000)
  }

  async _pollOnce () {
    const { scene_value, qrcodeStatus } = this._state
    if (!scene_value) return
    if (qrcodeStatus === QRCODE_STATUS.EXPIRED || qrcodeStatus === QRCODE_STATUS.SUCCESS) {
      this.stopPolling()
      return
    }

    try {
      const res = await loginApi.queryToken({ scene_value })
      if (!res) return

      const code = res.code
      const responseData = resolveAuthPayload(res)

      if (code === 1) {
        const { token, member } = applyQrcodeAuth(this.store, responseData)
        if (token) {
          this.stopPolling()
          this.stopExpireTimer()
          this._patch({ qrcodeStatus: QRCODE_STATUS.SUCCESS })
          await this.onLoginSuccess(res, member)
        }
      } else if (code === 0) {
        const msg = res?.msg || ''
        if (isExpiredMessage(msg)) {
          this._markExpired()
        }
      }
    } catch (err) {
      console.error('查询扫码结果失败', err)
      const errMsg = err?.msg || err?.message || ''
      if (isExpiredMessage(errMsg)) {
        this._markExpired()
      }
    }
  }
}
